import React, { useState, useEffect } from 'react';
import Popup from '../Popup/Popup';

interface CookieConsentProps {
    onAccept: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already given consent
        const hasConsent = localStorage.getItem('cookieConsent');
        if (!hasConsent) {
            // Show popup after a short delay to ensure page is loaded
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        } else if (hasConsent === 'true') {
            // If consent was already given, trigger the callback
            onAccept();
        }
    }, [onAccept]);

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'true');
        setIsVisible(false);
        onAccept();
    };

    const handleDecline = () => {
        localStorage.setItem('cookieConsent', 'false');
        setIsVisible(false);
    };

    const header = (
        <h2 id="cookie-consent-title" className="text-xl font-bold text-gray-800">
            <span role="img" aria-label="Cookie icon">🍪</span> Cookie Consent Required
        </h2>
    );

    const content = (
        <div id="cookie-consent-description" className="text-gray-600">
            <p className="mb-3">
                We use cookies to enhance your experience and enable our contact forms.
            </p>
            <p className="mb-3 font-medium text-gray-800">
                <span role="img" aria-label="Warning">⚠️</span> Important: If you don't accept cookies, you won't be able to use our contact forms.
            </p>
            <p>
                You can still reach us directly via email at{' '}
                <a
                    href="mailto:support@cloudixmedia.com?subject=Accessibility Services Inquiry"
                    className="text-blue-600 hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-1"
                >
                    support@cloudixmedia.com
                </a>
            </p>
        </div>
    );

    const footer = (
        <div className="flex flex-col sm:flex-row gap-3" role="group" aria-label="Cookie consent options">
            <button
                onClick={handleAccept}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 focus:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                aria-describedby="cookie-consent-description"
                type="button"
                autoFocus
            >
                Accept Cookies & Enable Forms
            </button>
            <button
                onClick={handleDecline}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 focus:bg-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                aria-describedby="cookie-consent-description"
                type="button"
            >
                Decline (Email Only)
            </button>
        </div>
    );

    return (
        <Popup
            isOpen={isVisible}
            header={header}
            footer={footer}
            ariaLabelledBy="cookie-consent-title"
            ariaDescribedBy="cookie-consent-description"
        >
            {content}
        </Popup>
    );
};

export default CookieConsent;